import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Stack,
  Tooltip
} from "@mui/material";
import { Delete as DeleteIcon, Map as MapIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { User } from "../models/user";
import { RouteResponse } from "../network/prompts_api";
import { getLoggedInUser, updateUser } from "../network/notes_api";

type RoutesPageProps = {
  user: User | null;
};

const RoutesPage: React.FC<RoutesPageProps> = ({ user }) => {
  const navigate = useNavigate();

  const handleOpenRoute = (route: RouteResponse) => {
    navigate("/", { state: { route } });
  };

  async function deleteRoute(route: RouteResponse) {
    if (!route) {
      alert("No route provided to delete.");
      return;
    }

    try {
      const user = await getLoggedInUser();
      const existingRoutes = user.routes || [];

      const updatedRoutes = existingRoutes.filter(
        (r) =>
          JSON.stringify(r.route) !== JSON.stringify(route.route) ||
          r.subjective !== route.subjective
      );

      await updateUser({ routes: updatedRoutes });
      alert("Route deleted!");
    } catch (error) {
      console.error("Failed to delete route:", error);
      alert("Failed to delete route. Please try again.");
    }
  }

  if (!user?.routes) return null;

  const maxRoutes =
    user.subscriptionStatus === "pro"
      ? 10
      : user.subscriptionStatus === "premium"
      ? 25
      : 1;

  return (
    <Box minHeight="100vh" py={6} px={{ xs: 2, md: 6 }} bgcolor="#f9f9f9">
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Saved Routes ({user.routes.length} / {maxRoutes} Max)
      </Typography>

      {user.routes.length === 0 ? (
        <Typography color="text.secondary">
          No saved routes yet. Start exploring and save your journey.
        </Typography>
      ) : (
        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent="center"
          gap={4}
          mt={2}
        >
          {user.routes.map((route, index) => (
            <Card
              key={index}
              elevation={3}
              sx={{
                width: { xs: "100%", sm: "100%", lg: "100%" },
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                borderRadius: 3,
              }}
            >
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {route.name ?? "Unnamed Route"}
                </Typography>

                <Box mb={1}>
                  <Typography variant="body2" fontWeight={500}>
                    Waypoints:
                  </Typography>
                  <ul style={{ marginLeft: "1em" }}>
                    {route.waypoints.map((wp, i) => (
                      <li key={i}>
                        {wp.name} ({wp.coordinates[0]}, {wp.coordinates[1]})
                      </li>
                    ))}
                  </ul>
                </Box>

                <Typography variant="body2">
                  <strong>Original Prompt:</strong> {route.prompt}
                </Typography>
                <Typography variant="body2">
                  <strong>Description:</strong> {route.description}
                </Typography>
                <Typography variant="body2">
                  <strong>Subjective:</strong> {route.subjective}
                </Typography>
                {/* <Typography variant="body2" mb={2}>
                  <strong>Avoid Highways:</strong>{" "}
                  {route.avoidHighways ? "Yes" : "No"}
                </Typography> */}
              </CardContent>

              <Stack
                direction="row"
                spacing={2}
                justifyContent="space-between"
                alignItems="center"
                px={2}
                pb={2}
              >
                <Tooltip title="View Route">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<MapIcon />}
                    onClick={() => handleOpenRoute(route)}
                  >
                    Open
                  </Button>
                </Tooltip>

                <Tooltip title="Delete Route">
                  <IconButton
                    color="error"
                    onClick={() => deleteRoute(route)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default RoutesPage;
